// ============================================================================
// NPSDClientConnections.tsx - Connections management component for Client
// Converted from Vue 2 Vue-A2032-NPSDClientConnections.ts
//
// Handles Client-to-Business, Client-to-Person (with roles),
// and Client-to-Vehicle connections.
// Uses shared NPSDConnectionPanel component for each panel.
// ============================================================================
import { useState, useCallback, useMemo } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { ConfirmModal } from '@/components/ConfirmModal';
import { NPSDConnectionPanel } from '@/components/npsd/NPSDConnectionPanel';

interface DeleteConfirmState {
  open: boolean;
  connectionType: string;
  item: any;
  displayName: string;
}

interface NPSDClientConnectionsProps {
  connections: any;
  isReadOnly?: boolean;
  onAddConnection?: (type: string) => void;
  onViewConnection?: (type: string, item: any) => void;
  onRemoveConnection?: (pluralType: string, item: any) => void;
}

export function NPSDClientConnections({
  connections,
  isReadOnly = false,
  onAddConnection,
  onViewConnection,
  onRemoveConnection,
}: NPSDClientConnectionsProps) {
  const { theme } = useTheme();

  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState>({
    open: false,
    connectionType: '',
    item: null,
    displayName: '',
  });

  // Computed: connection arrays
  const people = useMemo(
    () => connections?.people || [],
    [connections?.people]
  );

  const vehicles = useMemo(
    () => connections?.vehicles || [],
    [connections?.vehicles]
  );

  const businesses = useMemo(
    () => connections?.businesses || [],
    [connections?.businesses]
  );

  // Computed: isEditMode is the inverse of isReadOnly
  const isEditMode = useMemo(() => !isReadOnly, [isReadOnly]);

  // Add connection - callback to parent
  const handleAddConnection = useCallback(
    (connectionType: string) => {
      onAddConnection?.(connectionType);
    },
    [onAddConnection]
  );

  // View item - callback to parent
  const handleViewItem = useCallback(
    (connectionType: string, item: any) => {
      onViewConnection?.(connectionType, item);
    },
    [onViewConnection]
  );

  // Remove connection - open confirmation
  const handleRemoveConnection = useCallback(
    (connectionType: string, item: any) => {
      if (isReadOnly) return;

      let displayName = '';
      // connectionType from panel is singular ('person', 'vehicle', 'business')
      if (connectionType === 'person') {
        displayName = `${item.firstName || ''} ${item.lastName || ''}`.trim() || 'this person';
      } else if (connectionType === 'vehicle') {
        displayName = `${item.plateNumber || ''} (${item.plateState || ''})`.trim() || 'this vehicle';
      } else if (connectionType === 'business') {
        displayName = item.businessName || 'this business';
      }

      setDeleteConfirm({
        open: true,
        connectionType,
        item,
        displayName,
      });
    },
    [isReadOnly]
  );

  // Confirm delete
  const confirmDelete = useCallback(() => {
    const { connectionType, item } = deleteConfirm;
    if (!item) {
      setDeleteConfirm({ open: false, connectionType: '', item: null, displayName: '' });
      return;
    }

    // Emit with plural type to match original API
    const pluralType =
      connectionType === 'person'
        ? 'people'
        : connectionType === 'vehicle'
          ? 'vehicles'
          : connectionType === 'business'
            ? 'businesses'
            : connectionType;

    onRemoveConnection?.(pluralType, item);
    setDeleteConfirm({ open: false, connectionType: '', item: null, displayName: '' });
  }, [deleteConfirm, onRemoveConnection]);

  // Cancel delete
  const cancelDelete = useCallback(() => {
    setDeleteConfirm({ open: false, connectionType: '', item: null, displayName: '' });
  }, []);

  return (
    <div className={`npsd-client-connections${theme ? ' ' + theme : ''}`}>
      {/* Business Connections */}
      <NPSDConnectionPanel
        connections={businesses}
        connectionType="business"
        title="Business Connections"
        isEditMode={isEditMode}
        onAddConnection={handleAddConnection}
        onViewItem={handleViewItem}
        onRemoveConnection={handleRemoveConnection}
      />

      {/* Person Connections */}
      <NPSDConnectionPanel
        connections={people}
        connectionType="person"
        title="Person Connections"
        isEditMode={isEditMode}
        showRoles={true}
        onAddConnection={handleAddConnection}
        onViewItem={handleViewItem}
        onRemoveConnection={handleRemoveConnection}
      />

      {/* Vehicle Connections */}
      <NPSDConnectionPanel
        connections={vehicles}
        connectionType="vehicle"
        title="Vehicle Connections"
        isEditMode={isEditMode}
        onAddConnection={handleAddConnection}
        onViewItem={handleViewItem}
        onRemoveConnection={handleRemoveConnection}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={deleteConfirm.open}
        title={`Remove ${deleteConfirm.displayName}?`}
        messageHtml="Are you sure you want to remove this connection?"
        confirmText="Remove"
        cancelText="Cancel"
        confirmClass="danger"
        autoFocus="cancel"
        scrollLock={true}
        theme={theme}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
}

(window as any).NPSDClientConnectionsReact = NPSDClientConnections;
